import { api } from './api';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AiModel {
  id: string;
  name: string;
  provider: string;
  tier: string;
}

export interface AiProviderStatus {
  hasAdminKey: boolean;
  hasEnvKey: boolean;
  isEnabled: boolean;
  keySource: 'admin' | 'system' | 'none';
  models: AiModel[];
  updatedAt: string | null;
}

export interface AiModuleConfig {
  id: string;
  module: string;
  provider: string;
  modelId: string;
  isEnabled: boolean;
}

export interface AiUsageStats {
  totalInputTokens: number;
  totalOutputTokens: number;
  totalTokens: number;
  totalRequests: number;
}

export interface AiUsageByUser {
  employeeId: string;
  name: string;
  avatarUrl: string | null;
  totalTokens: number;
  inputTokens: number;
  outputTokens: number;
  requests: number;
}

export interface AiUsageByModule {
  module: string;
  model: string;
  _sum: { inputTokens: number; outputTokens: number };
  _count: number;
}

// ─── API Functions ────────────────────────────────────────────────────────────

// Provider (OpenRouter)
export async function getAiProvider(): Promise<AiProviderStatus> {
  const { data } = await api.get('/ai/admin/provider');
  return data;
}

export async function setAiApiKey(apiKey: string) {
  const { data } = await api.post('/ai/admin/provider/key', { apiKey });
  return data;
}

export async function removeAiApiKey() {
  const { data } = await api.delete('/ai/admin/provider/key');
  return data;
}

export async function testAiConnection(): Promise<{ success: boolean; error?: string }> {
  const { data } = await api.post('/ai/admin/provider/test');
  return data;
}

// Module configs
export async function getAiModuleConfigs(): Promise<AiModuleConfig[]> {
  const { data } = await api.get('/ai/admin/modules');
  return data;
}

export async function updateAiModuleConfig(
  module: string,
  dto: { modelId: string; isEnabled?: boolean },
) {
  const { data } = await api.patch(`/ai/admin/modules/${module}`, dto);
  return data;
}

export async function toggleAiModule(module: string, isEnabled: boolean) {
  const { data } = await api.put(`/ai/admin/modules/${module}/toggle`, { isEnabled });
  return data;
}

// Usage
export async function getAiUsageStats(from?: string, to?: string): Promise<AiUsageStats> {
  const { data } = await api.get('/ai/admin/usage', { params: { from, to } });
  return data;
}

export async function getAiUsageByUser(from?: string, to?: string): Promise<AiUsageByUser[]> {
  const { data } = await api.get('/ai/admin/usage/by-user', { params: { from, to } });
  return data;
}

export async function getAiUsageByModule(from?: string, to?: string): Promise<AiUsageByModule[]> {
  const { data } = await api.get('/ai/admin/usage/by-module', { params: { from, to } });
  return data;
}
